import React, { useState, useContext, useEffect, useRef } from 'react';
import { Paper, InputBase, IconButton } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTranslation } from 'react-i18next';
import { MapContext } from '../../contexts/MapContext';
import { buscarPorCodigo, processarDadosPropriedade } from '../../services/PropertyService';
import { getUserData } from '../../services/AuthService';
import { logUserSearch } from '../../services/ActivityLogService';
import { propertyApi } from '../../services/BaseApiService.jsx';
import GeometryService from '../../services/GeometryService.js';
import { trackPropertyProgress } from '../../services/PropertyProgressTracker';

function UnifiedSearchBar() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const {
    setPropertyData,
    setGeometries,
    setSidebarOpen,
    setThemeGeometries,
    setActiveLayers,
    setPropertyCodes,
    setMarker,
    setProcessStatus,
    setRetryProcessingSearch,
    _handlePropertyResult,
    geometries,
    marker,
  } = useContext(MapContext);
  const [loading, setLoading] = useState(false);
  const [showNoGeojsonAlert, setShowNoGeojsonAlert] = useState(false);
  const [showCoordFormatAlert, setShowCoordFormatAlert] = useState(false);
  const [userId, setUserId] = useState(null);
  const pollingRef = useRef(null);

  // pollingRef guarda a função `cancel()` retornada por trackPropertyProgress
  // (SSE via consolidate-service `/events/status/{fid}` — D-SL-02).
  const clearPolling = () => {
    if (pollingRef.current) {
      try {
        pollingRef.current();
      } catch (_) {
        /* no-op */
      }
      pollingRef.current = null;
    }
  };

  // Obter o ID do usuário ao carregar o componente
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getUserData();
        if (userData && userData.id) {
          setUserId(userData.id);
        }
      } catch (error) {
        console.error('Erro ao obter dados do usuário:', error);
      }
    };

    fetchUserData();
  }, []);

  // Cleanup do polling ao desmontar
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, []);

  // Esconde o alerta automaticamente quando geometrias forem carregadas por qualquer fluxo (busca ou clique)
  useEffect(() => {
    try {
      if (Array.isArray(geometries) && geometries.length > 0) {
        setShowNoGeojsonAlert(false);
      }
    } catch (_) {
      /* no-op */
    }
  }, [geometries]);

  // Esconde o alerta quando um novo clique inicia processamento (marcador amarelo) ou o marcador muda de posição
  useEffect(() => {
    try {
      if (marker && (marker.processing === true || Array.isArray(marker.position))) {
        setShowNoGeojsonAlert(false);
      }
    } catch (_) {
      /* no-op */
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker?.processing, marker?.position?.[0], marker?.position?.[1]]);

  // Função para registrar a atividade de busca
  const logSearch = async (searchParams) => {
    try {
      if (userId) {
        await logUserSearch(userId, searchParams);
      }
    } catch (error) {
      // Apenas log do erro, não interrompe o fluxo principal
      console.error('Erro ao registrar busca:', error);
    }
  };

  /**
   * Verifica se a query é uma coordenada válida no formato lat,lon
   * @param {string} query - String a ser verificada
   * @returns {object|null} - Objeto com lat e lng se válido, null se não
   */
  const parseCoordinates = (query) => {
    if (!query || !query.includes(',')) return null;

    try {
      // Divide a string por vírgula e converte para números
      const [latStr, lngStr] = query.trim().split(',');
      const lat = parseFloat(latStr.trim());
      const lng = parseFloat(lngStr.trim());

      // Valida se são números e estão dentro dos limites válidos
      if (isNaN(lat) || isNaN(lng)) return null;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

      return { lat, lng };
    } catch (err) {
      console.error('[UnifiedSearchBar] Erro ao converter coordenadas:', err);
      return null;
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    // Limpa o campo de input ao iniciar a busca
    const busca = query.trim();
    setQuery('');

    if (!busca) return;

    // Esconder alertas anteriores
    setShowCoordFormatAlert(false);
    setShowNoGeojsonAlert(false);

    // Limpa contextos e states de pesquisa anterior
    setPropertyData(null);
    setGeometries([]);
    setThemeGeometries({});
    setActiveLayers({});
    setSidebarOpen(false);
    setMarker(null);
    setProcessStatus(null);
    setRetryProcessingSearch(null);
    clearPolling();

    try {
      // Verifica se é uma coordenada no formato lat,lon
      const coordinates = parseCoordinates(busca);

      // Detecta tipo de código e monta o parâmetro correto com os nomes esperados pelo backend
      let params = {};

      if (coordinates) {
        // Se for coordenadas, usar os parâmetros de latitude e longitude
        params = { latitude: coordinates.lat, longitude: coordinates.lng };
      } else if (/^[A-Z]{2}-\d{7}-[A-F0-9]{32}$/i.test(busca)) {
        params = { cod_imovel: busca };
      } else if (/^\d{13}$/.test(busca)) {
        params = { codigo_imo: busca };
      } else if (busca.includes(',')) {
        // É uma tentativa de inserir coordenadas, mas o formato é inválido
        setShowCoordFormatAlert(true);
        setLoading(false);
        return;
      } else {
        params = { parcela_co: busca };
      }

      // Registra a busca no sistema de log
      logSearch(params);

      // Define o handler que será chamado pelo buscarPorCodigo
      const handleResult = (propertyData, geometryData, extraData) => {
        if (!propertyData || !geometryData) {
          console.warn('[UnifiedSearchBar] Dados inválidos recebidos no handler');
          return;
        }

        //console.log('[UnifiedSearchBar] Handler de processamento chamado após busca');

        // Extrair metadados de arquivo para o contexto
        const fileId =
          propertyData._file_id || (propertyData._metadata && propertyData._metadata.file_id);
        const filename = fileId ? `${fileId}.json` : null;

        // Definir a propriedade imediatamente (geometrias renderizam),
        // mas manter o marcador em amarelo até process_succeeded.
        setPropertyData(propertyData);

        // Detectar a presença de geometrias a partir do geometryData já processado
        // Isso cobre corretamente car[].themes[].geojson e sigef[].parcels[].geojson
        const totalGeometrias = geometryData?.geometries?.length || 0;
        setShowNoGeojsonAlert(totalGeometrias === 0);

        // Atualiza os códigos e o filename no contexto global
        setPropertyCodes({
          cod_imovel: extraData?.cod_imovel || null,
          codigo_imo: extraData?.codigo_imo || null,
          filename: filename,
        });

        // Define o marcador como PROCESSANDO (amarelo) baseado no centroid calculado
        const fid =
          fileId ||
          (extraData?.filename ? String(extraData.filename).replace(/\.json$/, '') : null);
        const markerPos = extraData?.centroid
          ? [extraData.centroid.lat, extraData.centroid.lng]
          : null;
        const acceptedStatus = String(extraData?.search_accepted_status || '').toLowerCase();
        const isImmediateReady = acceptedStatus === 'succeeded';
        if (markerPos) {
          setMarker({
            position: markerPos,
            processing: !isImmediateReady,
            found: isImmediateReady ? true : null,
            data: null,
            properties: fid ? { filename: `${fid}.json` } : undefined,
          });
        }

        if (isImmediateReady) {
          setProcessStatus(null);
          setRetryProcessingSearch(null);
        } else {
          setProcessStatus({
            file_id: fid || null,
            request_id: extraData?.request_id || null,
            pending_count: null,
            domains_processed_count: 0,
            domains_processed: [],
            final_status: null,
            last_status: 'processing_started',
            can_retry: false,
          });

          setRetryProcessingSearch(() => async () => {
            try {
              clearPolling();
              setProcessStatus((prev) => ({
                ...(prev || {}),
                final_status: null,
                can_retry: false,
                last_status: 'retry_requested',
              }));
              await buscarPorCodigo(
                { ...params, force_refresh: true },
                setPropertyData,
                setLoading,
                handleResult,
              );
            } catch (_) {
              // mantém status atual
            }
          });
        }

        // Define as camadas ativas baseado nas geometrias encontradas
        const newActiveLayers = {};
        const standardLayers = [
          'ÁREA DO IMÓVEL',
          'SIGEF',
          'ÁREA CONSOLIDADA',
          'VEGETAÇÃO NATIVA',
          'RESERVA LEGAL',
          'APP',
          'HIDROGRAFIA',
          'ÁREA DE SERVIDÃO',
          'ÁREA DE POUSIO',
          'USO RESTRITO',
        ];

        // Inicializa todas as camadas padrão como ativas
        standardLayers.forEach((layer) => {
          newActiveLayers[layer] = true;
        });

        // Garante que camadas com geometrias encontradas estejam ativas
        Object.keys(geometryData?.themeGeometries || {}).forEach((tema) => {
          if (geometryData.themeGeometries[tema] && geometryData.themeGeometries[tema].length > 0) {
            newActiveLayers[tema] = true;
          }
        });

        // IMPORTANTE: Atualiza as geometrias no MapContext (igual ao handler do clique no mapa)
        //console.log('[UnifiedSearchBar] Atualizando geometrias no contexto:', {
        //  totalGeometrias: geometryData.geometries ? geometryData.geometries.length : 0,
        //  temasGeometrias: Object.keys(geometryData.themeGeometries || {})
        //});
        setGeometries(geometryData.geometries); // Geometrias por fonte
        setThemeGeometries(geometryData.themeGeometries); // Geometrias por tema

        //console.log('[UnifiedSearchBar] Definindo camadas ativas:', Object.keys(newActiveLayers));
        setActiveLayers(newActiveLayers);

        // Acompanhamento via SSE no consolidate-service (D-SL-02). Substitui
        // polling em /api/v1/process/{fid} (depreciado em 2026-05-23 com
        // refactor track/SSE — F-PLAT-REDIS-09).
        try {
          if (fid && !isImmediateReady && !pollingRef.current) {
            pollingRef.current = trackPropertyProgress(fid, {
              requestId: extraData?.request_id || null,
              onSnapshot: (snapshot) => setProcessStatus(snapshot),
              onTerminal: async ({ finalStatus, failed }) => {
                if (failed) {
                  clearPolling();
                  return;
                }
                if (finalStatus !== 'process_succeeded' && finalStatus !== 'cache_hit') {
                  return;
                }
                clearPolling();
                setProcessStatus((prev) => ({
                  ...(prev || {}),
                  pending_count: 0,
                  last_status: finalStatus,
                  final_status: finalStatus,
                  can_retry: false,
                }));
                // Refresh do STATE final antes de alterar o marcador
                try {
                  const jsonRes = await propertyApi.get(`/api/v1/state/${fid}`);
                  const consolidated = jsonRes?.data;
                  const propertyDataFinal = processarDadosPropriedade(consolidated);
                  setPropertyData(propertyDataFinal);
                  const geometry = GeometryService.processPropertyGeometries(propertyDataFinal);
                  setGeometries(geometry.geometries || []);
                  setThemeGeometries(geometry.themeGeometries || {});
                  setShowNoGeojsonAlert((geometry?.geometries?.length || 0) === 0);
                  try {
                    const newActiveLayers = {};
                    const standardLayers = [
                      'ÁREA DO IMÓVEL',
                      'SIGEF',
                      'ÁREA CONSOLIDADA',
                      'VEGETAÇÃO NATIVA',
                      'RESERVA LEGAL',
                      'APP',
                      'HIDROGRAFIA',
                      'ÁREA DE SERVIDÃO',
                      'ÁREA DE POUSIO',
                      'USO RESTRITO',
                    ];
                    standardLayers.forEach((layer) => {
                      newActiveLayers[layer] = true;
                    });
                    Object.keys(geometry?.themeGeometries || {}).forEach((tema) => {
                      if (geometry.themeGeometries[tema]?.length > 0) newActiveLayers[tema] = true;
                    });
                    setActiveLayers(newActiveLayers);
                  } catch (_) {
                    /* no-op */
                  }
                } catch (_) {
                  /* no-op */
                }
                if (markerPos) {
                  setMarker({
                    position: markerPos,
                    processing: false,
                    found: true,
                    data: null,
                    properties: { filename: `${fid}.json` },
                  });
                } else {
                  setMarker((prev) =>
                    prev
                      ? {
                          ...prev,
                          processing: false,
                          found: true,
                          properties: { filename: `${fid}.json` },
                        }
                      : prev,
                  );
                }
              },
            });
          }
        } catch (_) {
          /* no-op */
        }
      };

      // Busca dados usando o serviço unificado que agora processa TUDO identicamente
      await buscarPorCodigo(params, setPropertyData, setLoading, handleResult);

      //console.log('[UnifiedSearchBar] Busca concluída');

      // O estado de loading será controlado pelo PropertyService durante o polling
    } catch (err) {
      setPropertyData(null);
      setGeometries([]);
      setThemeGeometries({});
      setActiveLayers({});
      setSidebarOpen(false);
      setProcessStatus(null);
      setRetryProcessingSearch(null);
      if (err.isPropertyNotFound) {
        const codeValue =
          params.cod_imovel ||
          params.codigo_imo ||
          err?.searchParams?.cod_imovel ||
          err?.searchParams?.codigo_imo;
        const hasCodeSearch = !!codeValue;
        let markerPosition = null;
        if (!hasCodeSearch && params.latitude && params.longitude) {
          markerPosition = [params.latitude, params.longitude];
        } else if (
          !hasCodeSearch &&
          err.searchParams &&
          err.searchParams.latitude &&
          err.searchParams.longitude
        ) {
          markerPosition = [err.searchParams.latitude, err.searchParams.longitude];
        }
        if (markerPosition) {
          setMarker({ position: markerPosition, found: false, data: null });
        } else {
          setMarker(null);
        }
        if (hasCodeSearch) {
          alert(`Nenhuma Propriedade Rural encontrada com o código ${codeValue}`);
        } else {
          alert(err.message || 'Propriedade não encontrada nas coordenadas informadas.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showNoGeojsonAlert && (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningAmberIcon />}
          onClose={() => setShowNoGeojsonAlert(false)}
          sx={{
            position: 'absolute',
            top: 84,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            width: 'auto',
            maxWidth: '90%',
            minWidth: 320,
            boxShadow: 3,
            mt: 1,
          }}
        >
          <AlertTitle>Propriedade sem georreferenciamento</AlertTitle>
          Não é possível visualizar esta propriedade no mapa.
        </Alert>
      )}
      {showCoordFormatAlert && (
        <Alert
          severity="info"
          variant="filled"
          icon={<LocationOnIcon />}
          sx={{
            position: 'absolute',
            top: 84,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            width: 'auto',
            maxWidth: '90%',
            minWidth: 320,
            boxShadow: 3,
            mt: 1,
          }}
          onClose={() => setShowCoordFormatAlert(false)}
        >
          <AlertTitle>Formato de coordenadas inválido</AlertTitle>
          Digite no formato: latitude,longitude (ex: -15.789,-47.925)
        </Alert>
      )}
      <Paper
        component="form"
        onSubmit={handleSearch}
        sx={{
          p: '2px 8px',
          display: 'flex',
          alignItems: 'center',
          width: 420,
          position: 'absolute',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
          boxShadow: 3,
          borderRadius: 4,
        }}
        elevation={6}
      >
        <InputBase
          id="unified-search-input"
          name="query"
          sx={{ ml: 1, flex: 1 }}
          placeholder={t('Car, Incra, Parcela, Coordenadas (Lat,Lon)')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading}
          inputProps={{ 'aria-label': t('Car, Incra, Parcela, Coordenadas (Lat,Lon)') }}
          autoFocus
        />
        <IconButton type="submit" sx={{ p: '10px' }} aria-label="search" disabled={loading}>
          <SearchIcon />
        </IconButton>
      </Paper>
    </>
  );
}

export default UnifiedSearchBar;
